// ToolbarComponent.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
    Button,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    MenuList,
    CircularProgress,
    Chip
} from '@mui/material';
import { toast } from 'react-toastify';
import axios from 'axios';
import { API_BASE_PYTHON_SERVICE } from '../../utils/config';
import {
    obtenerCreditosAps,
    claveProducto,
    controlaTipo,
    formatHa,
    CREDITOS_APS_EVENTO,
    PRODUCTOS_CREDITOS,
    TIPOS_CON_CREDITOS,
} from '../../utils/creditosAps';

/* ─── Íconos ─────────────────────────────────────────── */
import ExcelIcon from '@mui/icons-material/InsertDriveFile';
import ShapefileIcon from '@mui/icons-material/Map';
import TemplateIcon from '@mui/icons-material/Download';
import AnalysisIcon from '@mui/icons-material/BarChart';
import { styled } from '@mui/material/styles';
import AutoModeIcon from '@mui/icons-material/AutoMode';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import StraightenIcon from '@mui/icons-material/Straighten';
import FlightIcon from '@mui/icons-material/FlightTakeoff';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import ScienceIcon from '@mui/icons-material/Science';
import GrassIcon from '@mui/icons-material/Grass';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import TableChartIcon from '@mui/icons-material/TableChart';

/* ─── Componentes auxiliares ─────────────────────────────────────────── */
import MetadatosDeteccionMalezaModal from '../MetadatosDeteccionMalezaModal/MetadatosDeteccionMalezaModal';
import FertilizacionModal from '../FertilizacionModal/FertilizacionModal';
import AplicacionesAereasModal from '../AplicacionesAereasModal/AplicacionesAereasModal';
import DatosEnvioAnalisisDialog from '../DatosEnvioAnalisis/DatosEnvioAnalisisDialog';
import analysisUtils from '../../utils/analysisConfig';
import { manejarSubidaZip } from '../../utils/fileHandler';
import { captureMapImage } from '../../analysis/AnalysisMapping/MappingGeneral';
import Loader from '../Loader/Loader';

/* ─── Estilos styled-components ─────────────────────── */
import {
    ToolbarContainer,
    ButtonGroup,
    StyledButton,
    IconButtonStyled,
    ButtonSection,
    TooltipStyled,
    StyledSelect
} from './ToolbarComponentStyle';

/* ─── Config compacta / responsive para la toolbar ──── */

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

const TOOLBAR_GAP = 10;
const COMPACT_BUTTON_HEIGHT = 36;

const baseCompactButtonStyle = {
    padding: '6px 14px',
    minHeight: COMPACT_BUTTON_HEIGHT,
    fontSize: 12,
    borderRadius: 999,
    whiteSpace: 'nowrap'
};

const primaryCompactButtonStyle = {
    ...baseCompactButtonStyle,
    fontWeight: 600
};

const iconOnlyCompactStyle = {
    padding: 4,
    minHeight: COMPACT_BUTTON_HEIGHT,
    minWidth: 60,
    borderRadius: 999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
};

const iconDarkStyle = {
    ...iconOnlyCompactStyle,
    backgroundColor: '#333',
    color: '#fff'
};

const iconDisabledStyle = {
    ...iconOnlyCompactStyle,
    backgroundColor: '#b6b6b6',
    color: '#fff'
};

const renderIconLabel = (iconElement, label) => (
    <div
        style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2
        }}
    >
        {iconElement}
        <span
            style={{
                fontSize: 10,
                fontWeight: 500,
                lineHeight: 1,
                letterSpacing: 0.2
            }}
        >
            {label}
        </span>
    </div>
);

/* ──────────────────────────────────────────────────────────────────────
 * Menú por defecto (cuando NO vienen opciones desde el sidebar)
 * ────────────────────────────────────────────────────────────────────── */
const DEFAULT_ANALYSIS_MENU = [
    {
        label: 'Aplicaciones Aéreas',
        value: 'APLICACIONES_AEREAS',
        icon: <FlightIcon fontSize="small" />,
        hint: 'KML/ZIP o ingreso manual'
    },
    {
        label: 'Cosecha Mecánica',
        value: 'COSECHA_MECANICA',
        icon: <PrecisionManufacturingIcon fontSize="small" />,
        hint: 'Líneas de pisoteo, buffers y métricas'
    },
    {
        label: 'Fertilización',
        value: 'FERTILIZACION',
        icon: <ScienceIcon fontSize="small" />,
        hint: 'ZIP/TXT • Ancho de faja • Rangos'
    },
    {
        label: 'Herbicidas',
        value: 'HERBICIDAS',
        icon: <GrassIcon fontSize="small" />,
        hint: 'KML/ZIP de franjas + datos manuales'
    },
    // Alias visible por pedido (mapea a APLICACIONES_AEREAS)
    {
        label: 'APS',
        value: 'APLICACIONES_AEREAS',
        icon: <FlightIcon fontSize="small" />,
        hint: 'Alias de Aplicaciones Aéreas'
    }
];

/* Mapeo de iconos/hints por tipo de análisis (para opciones dinámicas) */
const ICON_BY_ANALYSIS = {
    APLICACIONES_AEREAS: <FlightIcon fontSize="small" />,
    COSECHA_MECANICA: <PrecisionManufacturingIcon fontSize="small" />,
    FERTILIZACION: <ScienceIcon fontSize="small" />,
    HERBICIDAS: <GrassIcon fontSize="small" />,
    DETECCION_MALEZA: <GrassIcon fontSize="small" />,
    CONTEO_PALMA: <PrecisionManufacturingIcon fontSize="small" />,
    CONTEO_AGAVE: <ScienceIcon fontSize="small" />
};

const HINT_BY_ANALYSIS = {
    APLICACIONES_AEREAS: 'KML/ZIP o ingreso manual',
    COSECHA_MECANICA: 'Líneas de pisoteo, buffers y métricas',
    FERTILIZACION: 'ZIP/TXT • Ancho de faja • Rangos',
    HERBICIDAS: 'KML/ZIP de franjas + datos manuales',
    DETECCION_MALEZA: 'TIF/TIFF (imagen) + metadatos',
    CONTEO_PALMA: 'TIF/TIFF (imagen) • cuenta objetos',
    CONTEO_AGAVE: 'TIF/TIFF (imagen) • cuenta objetos'
};

/* Helper para “humanizar” etiquetas si vienen sólo los values */
const humanize = (val) =>
    String(val || '')
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/(^|\s)\S/g, (t) => t.toUpperCase());

const valueToLabel = (value, menu) => {
    const item = (menu || []).find((i) => i.value === value);
    return item ? item.label : 'Seleccionar análisis';
};

const ToolbarComponent = ({
                              selectedAnalysisType,
                              handleAnalysisTypeChange,
                              manejarSubidaArchivo,
                              uploadedCsvFileName,
                              uploadedZipFileName,
                              execBash,
                              activarEdicionInteractiva,
                              setActivarEdicionInteractiva,
                              isKMLFile,
                              execBashEnabled,
                              isSidebarOpen,
                              polygonsData = [],
                              highlightedLote,
                              activeLotes,
                              searchTerm,
                              setSearchTerm,
                              onHoverLote,
                              onLeaveLote,
                              onSelectLote,
                              clearAllLotes,
                              openFilterDialog,
                              processingFinished,
                              handleSendDashboardData,
                              analysisOptions,
                              setIsKMLFile,
                              setOpenSnackbar,
                              setSelectedZipFile,
                              setUploadedZipFileName,
                              selectedApsTargetPolygonFile,
                              setSelectedApsTargetPolygonFile,
                              uploadedApsTargetPolygonFileName,
                              setUploadedApsTargetPolygonFileName,
                              mapRef,
                              setImgLaflet,
                              setIsGeneratingReport,
                              mostrarLineas,
                              handleToggleMostrarLineas,
                              isGeneratingReport,
                              setCalidad,
                              calidad,
                              disableAnalysisSelection,
                              forceEnableUploadTemplate,
                              selectedSidebarOption,
                              currentReportAnalysisId,
                              hasGeneratedPdfForCurrentAnalysis = false,
                              shouldPromptGeneratePdf = false
                          }) => {
    /* ──────────────────── Estados internos ─────────────────── */
    const [openCommentModal, setOpenCommentModal] = useState(false);
    const [comment, setComment] = useState('');

    // Detección maleza
    const [openMetadatosModal, setOpenMetadatosModal] = useState(false);
    const [metadatosCompletados, setMetadatosCompletados] = useState(false);

    // Fertilización
    const [openFertModal, setOpenFertModal] = useState(false);
    const [anchoFajaConfirmado, setAnchoFajaConfirmado] = useState(false);
    const [txtFileUploaded, setTxtFileUploaded] = useState(false);

    // ZIP con puntos ⇒ mostrar solo "Ancho de faja"
    const [zipHasPoints, setZipHasPoints] = useState(false);
    const [fertModalMode, setFertModalMode] = useState('TXT');

    // Loader durante validación ZIP (fertilización)
    const [isZipValidating, setIsZipValidating] = useState(false);

    // Nombre del archivo de aplicación (ZIP/SHP/KML/KMZ/TXT/TIF/.log) tal como lo
    // acaba de elegir el usuario. Lo guardamos aquí además de avisar al padre para
    // que el botón se ponga en verde aunque el padre tarde o limpie su estado.
    const [localApplicationFileName, setLocalApplicationFileName] = useState('');

    // Nombre efectivo del archivo de aplicación y bandera para pintar el botón.
    const applicationFileName = uploadedZipFileName || localApplicationFileName;
    const hasApplicationFile = Boolean(applicationFileName);

    // Propiedades dinámicas por análisis (para PDF y fertilización)
    const [customProps, setCustomProps] = useState([]);

    // Aplicaciones Aéreas (modal manual)
    const [openApsModal, setOpenApsModal] = useState(false);
    const [apsRowsCount, setApsRowsCount] = useState(0);
    const [apsInitialRows, setApsInitialRows] = useState([]);

    // Dropdown "Seleccionar análisis"
    const [anchorEl, setAnchorEl] = useState(null);
    const analysisMenuOpen = Boolean(anchorEl);

    // Herbicidas reutiliza los selectores de Aplicaciones Aéreas (producto,
    // maquinaria y modo) con sus propias claves de almacenamiento y con
    // maquinaria limitada a Dron / Autopropulsado. APS no cambia.
    const isHerbicidasAnalysis = selectedAnalysisType === 'HERBICIDAS';
    const isAerialLikeAnalysis = selectedAnalysisType === 'APLICACIONES_AEREAS' || isHerbicidasAnalysis;
    const storagePrefix = isHerbicidasAnalysis ? 'herbicidas' : 'aplicacionesAereas';
    const materialStorageKey = `${storagePrefix}_material_type`;
    const modeStorageKey = `${storagePrefix}_modo`;
    const machineryStorageKey = `${storagePrefix}_maquinaria`;
    const manualRowsStorageKey = `${storagePrefix}_manual_rows`;
    const modeChangedEventName = `${storagePrefix}:modo-actualizado`;

    const MACHINERY_OPTIONS_BY_ANALYSIS = {
        APLICACIONES_AEREAS: [
            { value: 'DRONE', label: 'Dron' },
            { value: 'HELICOPTERO', label: 'Helicóptero' },
            { value: 'AVIONETA', label: 'Avioneta' }
        ],
        HERBICIDAS: [
            { value: 'DRONE', label: 'Dron' },
            { value: 'AUTOPROPULSADO', label: 'Autopropulsado' }
        ]
    };
    const machineryOptions = MACHINERY_OPTIONS_BY_ANALYSIS[selectedAnalysisType] || MACHINERY_OPTIONS_BY_ANALYSIS.APLICACIONES_AEREAS;
    const machineryLabel = (val) => (machineryOptions.find((o) => o.value === val) || { label: String(val || '') }).label;

    // Tipo de material (APS / HERBICIDAS / FERTILIZACION)
    const [materialType, setMaterialType] = useState(() => {
        const savedMaterialType = localStorage.getItem('aplicacionesAereas_material_type');
        return savedMaterialType === 'SOLIDO' ? 'SOLIDO' : 'LIQUIDO';
    });
    const [apsApplicationMode, setApsApplicationMode] = useState(() => {
        const savedMode = localStorage.getItem('aplicacionesAereas_modo');
        return savedMode === 'VAR' ? 'VAR' : 'CONVENCIONAL';
    });

    const showMaterialSelector =
        isAerialLikeAnalysis ||
        selectedAnalysisType === 'FERTILIZACION';

    const [machineryType, setMachineryType] = useState('DRONE'); // APS: 'DRONE' | 'HELICOPTERO' | 'AVIONETA' — Herbicidas: 'DRONE' | 'AUTOPROPULSADO'
    const showMachinerySelector = isAerialLikeAnalysis;
    // Modo Normal/VRA con todos los equipos de APS y de Herbicidas. En VRA el
    // archivo de aplicación puede ser de puntos (SHP/ZIP) o de franjas
    // (KML/KMZ/ZIP): el servicio detecta cuál es y lo mapea igual.
    const showApplicationModeSelector = isAerialLikeAnalysis;
    const isVarModeActive = isAerialLikeAnalysis && apsApplicationMode === 'VAR';

    // Al cambiar de análisis, cargar las preferencias guardadas de ese análisis
    // (producto, maquinaria, modo) sin mezclar APS con Herbicidas.
    useEffect(() => {
        if (!isAerialLikeAnalysis) return;
        const savedMaterial = localStorage.getItem(materialStorageKey);
        setMaterialType(savedMaterial === 'SOLIDO' ? 'SOLIDO' : 'LIQUIDO');
        const savedMode = localStorage.getItem(modeStorageKey);
        setApsApplicationMode(savedMode === 'VAR' ? 'VAR' : 'CONVENCIONAL');
        const savedMachinery = localStorage.getItem(machineryStorageKey);
        const validMachinery = (MACHINERY_OPTIONS_BY_ANALYSIS[selectedAnalysisType] || []).some((o) => o.value === savedMachinery);
        setMachineryType(validMachinery ? savedMachinery : 'DRONE');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAnalysisType]);

    // ── Créditos de hectáreas por producto (todos los mapeos de maquinaria) ──
    // Si la empresa no controla ese mapeo, todo queda como antes.
    const [creditosAps, setCreditosAps] = useState(null);
    const tipoCreditos = TIPOS_CON_CREDITOS.has(selectedAnalysisType) ? selectedAnalysisType : null;
    useEffect(() => {
        if (!tipoCreditos) return undefined;
        let cancelado = false;
        const cargar = async () => {
            try {
                const userData = JSON.parse(localStorage.getItem('userData') || '{}');
                const data = await obtenerCreditosAps(userData?.userId);
                if (!cancelado) setCreditosAps(data);
            } catch (error) {
                console.warn('[CREDITOS_APS] No se pudo consultar el saldo:', error?.message);
            }
        };
        cargar();
        window.addEventListener(CREDITOS_APS_EVENTO, cargar);
        return () => {
            cancelado = true;
            window.removeEventListener(CREDITOS_APS_EVENTO, cargar);
        };
    }, [tipoCreditos]);

    const creditosActivos = Boolean(tipoCreditos) && controlaTipo(creditosAps, tipoCreditos);
    const creditoDe = (equipo, modo) => (creditosAps?.productos || [])
        .find((p) => p.producto === claveProducto(tipoCreditos, equipo, modo));
    const creditoActual = creditosActivos ? creditoDe(machineryType, apsApplicationMode) : null;
    const productoBloqueado = creditosActivos && !creditoActual?.habilitado;
    const motivoBloqueoCredito = !productoBloqueado ? '' : (
        creditoActual?.nivel === 'agotado'
            ? `Se agotaron las hectáreas de ${creditoActual.label}. Contacte a un administrador.`
            : `Su empresa no tiene hectáreas contratadas para ${creditoActual?.label || 'este producto'}.`
    );

    // Si la combinación guardada no tiene saldo, se pasa al primer producto
    // del mismo mapeo que sí tenga (solo donde hay selector de equipo y tasa).
    useEffect(() => {
        if (!creditosActivos || !productoBloqueado || !isAerialLikeAnalysis) return;
        const primero = PRODUCTOS_CREDITOS.find((def) => def.tipo === tipoCreditos
            && (creditosAps?.productos || []).some((p) => p.producto === def.producto && p.habilitado));
        if (!primero?.equipo) return;
        if (primero.equipo !== machineryType) handleMachineryTypeChange(primero.equipo);
        if (primero.modo !== apsApplicationMode) handleApsApplicationModeChange(null, primero.modo);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [creditosActivos, creditosAps, tipoCreditos]);

    // Nombre del análisis y correos extra del PDF (mapeos de maquinaria).
    const [datosEnvioAbierto, setDatosEnvioAbierto] = useState(false);
    const idUsuarioActual = (() => {
        try {
            return JSON.parse(localStorage.getItem('userData') || '{}')?.userId || null;
        } catch {
            return null;
        }
    })();

    const openMenu = (e) => setAnchorEl(e.currentTarget);
    const closeMenu = () => setAnchorEl(null);

    // Construye el menú que se mostrará
    const analysisMenuItems = useMemo(() => {
        if (Array.isArray(analysisOptions) && analysisOptions.length > 0) {
            return analysisOptions.map((opt) => {
                const value = typeof opt === 'string' ? opt : (opt.value || opt.key || '');
                const label = typeof opt === 'string' ? humanize(value) : (opt.label || humanize(value));
                const icon = ICON_BY_ANALYSIS[value] || <AnalysisIcon fontSize="small" />;
                const hint = (typeof opt === 'object' && opt.hint) || HINT_BY_ANALYSIS[value] || '';
                return { value, label, icon, hint };
            });
        }
        return DEFAULT_ANALYSIS_MENU;
    }, [analysisOptions]);

    // Limpia selección si ya no existe en el menú
    useEffect(() => {
        if (
            selectedAnalysisType &&
            !analysisMenuItems.some((i) => i.value === selectedAnalysisType)
        ) {
            handleAnalysisTypeChange({ target: { value: '' } });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [analysisMenuItems]);

    // Reinicios al cambiar de análisis
    useEffect(() => {
        // Limpiezas Maleza
        if (selectedAnalysisType !== 'DETECCION_MALEZA') {
            localStorage.removeItem('deteccionMaleza_datos_temp');
            localStorage.removeItem('deteccionMaleza_idTemp');
            localStorage.removeItem('deteccionMaleza_lotesData');
            localStorage.removeItem('deteccionMaleza_shapefileUrl');
            setMetadatosCompletados(false);
        }

        // Limpiezas Fertilización
        if (selectedAnalysisType !== 'FERTILIZACION') {
            setAnchoFajaConfirmado(false);
            setTxtFileUploaded(false);
            localStorage.removeItem('fertilizacion_ancho_faja');
            localStorage.removeItem('fertilizacion_rangos');
        }

        // Limpiezas APS manual
        if (selectedAnalysisType !== 'APLICACIONES_AEREAS') {
            localStorage.removeItem('aplicacionesAereas_manual_rows');
            localStorage.removeItem('aplicacionesAereas_manual_id');
        }
        // Limpiezas Herbicidas manual
        if (selectedAnalysisType !== 'HERBICIDAS') {
            localStorage.removeItem('herbicidas_manual_rows');
            localStorage.removeItem('herbicidas_manual_id');
        }
        if (selectedAnalysisType !== 'APLICACIONES_AEREAS' && selectedAnalysisType !== 'HERBICIDAS') {
            setApsRowsCount(0);
            setApsInitialRows([]);
        }

        setZipHasPoints(false);
        setFertModalMode('TXT');
        setIsZipValidating(false);

        // Cargar datos manuales de APS / Herbicidas si aplica
        if (selectedAnalysisType === 'APLICACIONES_AEREAS' || selectedAnalysisType === 'HERBICIDAS') {
            try {
                const rowsKey = selectedAnalysisType === 'HERBICIDAS' ? 'herbicidas_manual_rows' : 'aplicacionesAereas_manual_rows';
                const raw = localStorage.getItem(rowsKey) || '[]';
                const arr = JSON.parse(raw);
                setApsRowsCount(Array.isArray(arr) ? arr.length : 0);
                setApsInitialRows(Array.isArray(arr) ? arr : []);
            } catch {
                setApsRowsCount(0);
                setApsInitialRows([]);
            }
        }
    }, [selectedAnalysisType]);

    useEffect(() => {
        if (selectedAnalysisType === 'DETECCION_MALEZA') {
            if (localStorage.getItem('deteccionMaleza_datos_temp')) setMetadatosCompletados(true);
        }
        if (selectedAnalysisType === 'FERTILIZACION') {
            if (localStorage.getItem('fertilizacion_ancho_faja')) setAnchoFajaConfirmado(true);
        }
    }, [selectedAnalysisType]);

    useEffect(() => {
        if (selectedAnalysisType !== 'APLICACIONES_AEREAS' && selectedAnalysisType !== 'HERBICIDAS') {
            setApsApplicationMode('CONVENCIONAL');
            localStorage.setItem('aplicacionesAereas_modo', 'CONVENCIONAL');
            localStorage.setItem('herbicidas_modo', 'CONVENCIONAL');
        }
        // El archivo objetivo VRA no se comparte entre análisis.
        if (typeof setSelectedApsTargetPolygonFile === 'function') setSelectedApsTargetPolygonFile(null);
        if (typeof setUploadedApsTargetPolygonFileName === 'function') setUploadedApsTargetPolygonFileName('');
    }, [selectedAnalysisType, setSelectedApsTargetPolygonFile, setUploadedApsTargetPolygonFileName]);

    // El archivo de aplicación (KML/SHP/.log) tampoco se comparte entre análisis:
    // sin esto el botón quedaba en verde con el archivo del análisis anterior.
    // Solo se limpia en un cambio real de análisis, no al volver a montar.
    const previousAnalysisTypeRef = React.useRef(selectedAnalysisType);
    useEffect(() => {
        const previous = previousAnalysisTypeRef.current;
        previousAnalysisTypeRef.current = selectedAnalysisType;
        if (!previous || previous === selectedAnalysisType) return;
        if (typeof setSelectedZipFile === 'function') setSelectedZipFile(null);
        if (typeof setUploadedZipFileName === 'function') setUploadedZipFileName('');
        setLocalApplicationFileName('');
    }, [selectedAnalysisType, setSelectedZipFile, setUploadedZipFileName]);

    const handleMaterialTypeChange = (event) => {
        const nextMaterialType = event?.target?.value === 'SOLIDO' ? 'SOLIDO' : 'LIQUIDO';
        setMaterialType(nextMaterialType);
        localStorage.setItem(isAerialLikeAnalysis ? materialStorageKey : 'aplicacionesAereas_material_type', nextMaterialType);
    };

    const handleApsApplicationModeChange = (event, nextMode) => {
        if (!nextMode) return;
        setApsApplicationMode(nextMode);
        localStorage.setItem(modeStorageKey, nextMode);
        window.dispatchEvent(new CustomEvent(modeChangedEventName, { detail: { mode: nextMode } }));
        if (nextMode !== 'VAR') {
            if (typeof setSelectedApsTargetPolygonFile === 'function') setSelectedApsTargetPolygonFile(null);
            if (typeof setUploadedApsTargetPolygonFileName === 'function') setUploadedApsTargetPolygonFileName('');
        }
    };

    // El VRA no depende del equipo: todos aceptan puntos o franjas.
    const handleMachineryTypeChange = (nextMachineryType) => {
        setMachineryType(nextMachineryType);
        if (isAerialLikeAnalysis) localStorage.setItem(machineryStorageKey, nextMachineryType);
    };

    const handleUploadApsTargetPolygon = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!/\.(zip|kml|kmz)$/i.test(file.name)) {
            toast.warning('Para VRA, los polígonos de maleza deben subirse como SHP comprimido en ZIP o como KML/KMZ.');
            e.target.value = '';
            return;
        }
        if (typeof setSelectedApsTargetPolygonFile === 'function') setSelectedApsTargetPolygonFile(file);
        if (typeof setUploadedApsTargetPolygonFileName === 'function') setUploadedApsTargetPolygonFileName(file.name);
        toast.success(`Polígonos de maleza cargados: ${file.name}`, { autoClose: 2000 });
        e.target.value = '';
    };

    /* ──────────────────── Ruta plantilla ───────────────────── */
    const analysisKey =
        selectedAnalysisType && selectedAnalysisType.trim() !== ''
            ? selectedAnalysisType
            : selectedSidebarOption
                ? selectedSidebarOption
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
                    .replace(/\s+/g, '_')
                    .toUpperCase()
                : '';
    const templatePath = analysisUtils[analysisKey]?.templatePath || '#';
    const isApsAnalysis = analysisKey === 'APLICACIONES_AEREAS' || selectedAnalysisType === 'APLICACIONES_AEREAS';
    // Herbicidas tampoco usa plantilla: datos manuales + archivo de franjas.
    const canUseDataTemplate = !isApsAnalysis && !isHerbicidasAnalysis && selectedAnalysisType !== 'DETECCION_MALEZA';

    /* ──────────────────── Storage key para props dinámicas ───────────────────── */
    const storageKey = useMemo(
        () => `custom_props_${analysisKey || 'GLOBAL'}`,
        [analysisKey]
    );

    /* ──────────────────── Plantillas sugeridas ───────────────────── */
    const propertyTemplates = ['Supervisor proveedor', 'Supervisor de la empresa cliente'];

    /* ──────────────────── Cargar props dinámicas para el modal ───────────────── */
    useEffect(() => {
        try {
            const saved = JSON.parse(localStorage.getItem(storageKey) || '[]');
            setCustomProps(Array.isArray(saved) ? saved : []);
        } catch {
            setCustomProps([]);
        }
    }, [storageKey]);

    /* ───────────────────── PDF ───────────────────── */
    const handleGeneratePdf = () => setOpenCommentModal(true);
    const handleNuevoAnalisis = () => window.location.assign('/mapeo');
    const handleCloseComment = () => {
        setOpenCommentModal(false);
        setComment('');
    };
    const handleSubmitComment = () => {
        setOpenCommentModal(false);
        handleSendPdfWithComment();
    };
    const handleSendPdfWithComment = async () => {
        try {
            setIsGeneratingReport(true);
            if (mapRef.current) {
                const img = await captureMapImage(mapRef);
                setImgLaflet(img);

                // Adjuntamos props dinámicas como texto legible
                let finalComment = comment;
                try {
                    const extraProps = JSON.parse(localStorage.getItem(storageKey) || '[]');
                    const propsAsText = Array.isArray(extraProps)
                        ? extraProps
                            .filter((p) => (p?.label || '').trim() !== '')
                            .map((p) => `• ${p.label}: ${p.value || '-'}`)
                            .join('\n')
                        : '';
                    if (propsAsText) {
                        finalComment = `${comment || ''}\n\n---\nPropiedades:\n${propsAsText}`;
                    }
                } catch {
                    // seguir con comment original
                }

                await handleSendDashboardData(img, finalComment);
            }
        } catch (err) {
            console.error('Error generando PDF:', err);
            setIsGeneratingReport(false);
        }
    };

    const pdfTooltipTitle = !currentReportAnalysisId
        ? 'Generar informe en PDF'
        : shouldPromptGeneratePdf
            ? 'Aún no has generado el PDF de este análisis. Presiona aquí para generarlo y guardarlo automáticamente en Historial.'
            : hasGeneratedPdfForCurrentAnalysis
                ? 'PDF ya guardado en Historial. Puedes generar una nueva versión si lo necesitas.'
                : 'Generar informe en PDF';

    const pdfButtonStyle = shouldPromptGeneratePdf
        ? {
            ...iconDarkStyle,
            backgroundColor: '#fff7ed',
            color: '#c2410c',
            border: '1px solid #fb923c',
            boxShadow: '0 0 0 3px rgba(251, 146, 60, 0.18)'
        }
        : iconDarkStyle;

    /* ───────────────────── Upload plantilla ───────────────── */
    const handleUploadTemplate = (e) => {
        if (isApsAnalysis) {
            toast.info('Aplicaciones Aéreas ahora usa únicamente carga manual de datos.');
            e.target.value = '';
            return;
        }
        if (isHerbicidasAnalysis) {
            toast.info('Herbicidas usa carga manual de datos y el KML/ZIP de franjas de aplicación.');
            e.target.value = '';
            return;
        }

        const file = e.target.files[0];
        if (!file) return;
        if (!/(\.csv|\.xls|\.xlsx)$/i.test(file.name)) {
            toast.warning('Solo se permiten archivos CSV o Excel (.csv .xls .xlsx)');
            e.target.value = '';
            return;
        }
        if (selectedAnalysisType === 'DETECCION_MALEZA') setOpenMetadatosModal(true);
        else manejarSubidaArchivo(e);
    };

    /* ───────────────────── Helpers de archivo ───────────────────── */
    const getUploadButtonText = () => {
        if (isVarModeActive) {
            return 'Subir aplicación (puntos, franjas o .log)';
        }
        if (isHerbicidasAnalysis) {
            return 'Subir franjas (KML/ZIP/.log)';
        }
        if (
            selectedAnalysisType === 'DETECCION_MALEZA' ||
            selectedAnalysisType === 'CONTEO_PALMA' ||
            selectedAnalysisType === 'CONTEO_AGAVE'
        ) {
            return 'Subir Imagen TIF';
        }
        if (selectedAnalysisType === 'APLICACIONES_AEREAS') {
            return 'Subir Shapefile, KML o .log';
        }
        return 'Subir Shapefile';
    };

    const getAcceptedFileTypes = () => {
        if (isVarModeActive) {
            // VRA: puntos (SHP/ZIP), franjas (KML/KMZ/ZIP) o la bitácora del controlador
            // (.log o .log.txt, como la exportan algunos equipos).
            return '.zip,.kml,.kmz,.log,.txt';
        }
        if (isHerbicidasAnalysis) {
            return '.zip,.kml,.kmz,.log,.txt';
        }
        if (
            selectedAnalysisType === 'CONTEO_PALMA' ||
            selectedAnalysisType === 'CONTEO_AGAVE' ||
            selectedAnalysisType === 'DETECCION_MALEZA'
        ) {
            return '.tif,.tiff';
        }
        if (selectedAnalysisType === 'APLICACIONES_AEREAS') {
            // Modo Normal: shapefile, KML/KMZ o la bitácora del controlador.
            return '.zip,.txt,.kml,.kmz,.log';
        }
        return '.zip,.txt';
    };

    /* ───────────────────── Upload SHP/ZIP/TXT/TIF ─────────────── */
    const handleUploadShp = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (isVarModeActive) {
            if (!/\.(zip|kml|kmz|log)$|\.log\.txt$/i.test(file.name)) {
                toast.warning('Para VRA sube los puntos de aplicación (SHP comprimido en ZIP), las franjas (KML/KMZ o ZIP con el KML) o el .log del controlador.');
                e.target.value = '';
                return;
            }
        } else if (isHerbicidasAnalysis) {
            if (!/\.(zip|kml|kmz|log)$|\.log\.txt$/i.test(file.name)) {
                toast.warning('Para Herbicidas sube el KML/KMZ de franjas, un ZIP con el KML/SHP de aplicación o el .log del controlador.');
                e.target.value = '';
                return;
            }
        } else if (selectedAnalysisType === 'APLICACIONES_AEREAS') {
            if (!/\.(zip|txt|kml|kmz|log)$/i.test(file.name)) {
                toast.warning('Sube el shapefile (ZIP), el KML/KMZ o el .log del controlador.');
                e.target.value = '';
                return;
            }
        } else if (
            selectedAnalysisType === 'CONTEO_PALMA' ||
            selectedAnalysisType === 'CONTEO_AGAVE' ||
            selectedAnalysisType === 'DETECCION_MALEZA'
        ) {
            if (!/\.(tif|tiff)$/i.test(file.name)) {
                toast.warning('Solo se permiten archivos TIF (.tif, .tiff) para análisis de imágenes');
                e.target.value = '';
                return;
            }
        } else {
            if (!/\.(zip|txt)$/i.test(file.name)) {
                toast.warning('Solo se permiten archivos ZIP (.zip) o TXT (.txt)');
                e.target.value = '';
                return;
            }
        }

        // El archivo ya es válido: dejamos el botón en verde de inmediato, sin esperar
        // a la validación remota del ZIP ni al estado del componente padre.
        setLocalApplicationFileName(file.name);

        // Fertilización: detectar .txt para mostrar botón de datos
        if (selectedAnalysisType === 'FERTILIZACION') {
            if (/\.txt$/i.test(file.name)) {
                setTxtFileUploaded(true);
                setAnchoFajaConfirmado(false);
                localStorage.removeItem('fertilizacion_ancho_faja');
                localStorage.removeItem('fertilizacion_rangos');
                setZipHasPoints(false);
                setFertModalMode('TXT');
            } else {
                setTxtFileUploaded(false);
                setAnchoFajaConfirmado(false);
                localStorage.removeItem('fertilizacion_ancho_faja');
                localStorage.removeItem('fertilizacion_rangos');
            }
        }

        // Validar ZIP en Fertilización (puntos o no)
        if (selectedAnalysisType === 'FERTILIZACION' && /\.zip$/i.test(file.name)) {
            try {
                setIsZipValidating(true);
                const fd = new FormData();
                fd.append('archivo_zip', file);

                const { data } = await axios.post(
                    `${API_BASE_PYTHON_SERVICE}mapping/fertilizacion/validar_zip_shp`,
                    fd,
                    { headers: { 'Content-Type': 'multipart/form-data' } }
                );

                const hasPoints = !!(data && data.hasPoints);
                setZipHasPoints(hasPoints);

                if (hasPoints) {
                    setFertModalMode('ZIP_POINTS');
                    localStorage.setItem('fertilizacion_rangos', JSON.stringify([null, null, null]));
                    setOpenFertModal(true);
                } else {
                    setFertModalMode('TXT');
                }
            } catch (err) {
                console.error('Error validando ZIP de fertilización:', err);
                setZipHasPoints(false);
                setFertModalMode('TXT');
                toast.error('No se pudo validar el ZIP. Inténtalo de nuevo.');
            } finally {
                setIsZipValidating(false);
            }
        }

        if (isHerbicidasAnalysis && /\.(kml|kmz)$/i.test(file.name)) {
            // KML/KMZ suelto: no pasa por el lector ZIP del navegador.
            setSelectedZipFile(file);
            setUploadedZipFileName(file.name);
            if (typeof setOpenSnackbar === 'function') setOpenSnackbar(true);
            if (typeof setIsKMLFile === 'function') setIsKMLFile(true);
            e.target.value = '';
            return;
        }

        manejarSubidaZip(
            { target: { files: [file] } },
            setSelectedZipFile,
            setUploadedZipFileName,
            setOpenSnackbar,
            setIsKMLFile
        );
        e.target.value = '';
    };

    /* ───────────────────── Modales callbacks ──────────────── */
    // Maleza
    const onMalezaConfirm = (data) => {
        localStorage.setItem('deteccionMaleza_datos_temp', JSON.stringify(data));
        setMetadatosCompletados(true);
        setOpenMetadatosModal(false);
        toast.success('Datos de la finca guardados', { autoClose: 2500 });
    };

    // Fertilización (con propiedades dinámicas)
    const onFertConfirm = ({ anchoFaja, rangos, customProps: newCustomProps }) => {
        const clean = rangos.map((v) => (v === null ? null : Number(v)));
        localStorage.setItem('fertilizacion_ancho_faja', String(anchoFaja));
        localStorage.setItem('fertilizacion_rangos', JSON.stringify(clean));

        const propsClean = (newCustomProps || [])
            .filter((r) => r && String(r.label || '').trim() !== '')
            .map((r) => ({
                label: String(r.label || '').trim(),
                value: String(r.value ?? '').trim()
            }));
        localStorage.setItem(storageKey, JSON.stringify(propsClean));
        setCustomProps(propsClean);

        setAnchoFajaConfirmado(true);
        setOpenFertModal(false);
        toast.success('Datos de fertilización guardados', { autoClose: 2000 });
        window.dispatchEvent(new Event('fertilizacion:rangos-actualizados'));
    };

    // Aplicaciones Aéreas (guardar filas manuales)
    const onApsConfirm = (rows) => {
        try {
            localStorage.setItem(manualRowsStorageKey, JSON.stringify(rows));
            setApsRowsCount(rows.length);
            setApsInitialRows(rows);
            setOpenApsModal(false);
            // Dashboard escucha este evento para habilitar el botón Analizar (APS y Herbicidas).
            window.dispatchEvent(new Event('aplicacionesAereas:manual-rows-actualizadas'));
            toast.success(
                `Datos de ${isHerbicidasAnalysis ? 'herbicidas' : 'aplicaciones aéreas'} guardados (${rows.length} fila${rows.length !== 1 ? 's' : ''})`,
                { autoClose: 2000 }
            );
        } catch (err) {
            console.error('Error guardando filas APS:', err);
            toast.error('No se pudieron guardar los datos de aplicaciones aéreas.');
        }
    };

    /* ───────────────────── Calidad (solo conteo/maleza) ───── */
    const requiereCalidad =
        selectedAnalysisType === 'CONTEO_PALMA' ||
        selectedAnalysisType === 'CONTEO_AGAVE' ||
        selectedAnalysisType === 'DETECCION_MALEZA';

    /* ────────────────────────── Render ────────────────────── */
    return (
        <ToolbarContainer
            isSidebarOpen={isSidebarOpen}
            style={{
                padding: '6px 14px',
                gap: TOOLBAR_GAP,
                flexWrap: 'nowrap',
                alignItems: 'center',
                overflowX: 'auto',
                overflowY: 'hidden',
                minHeight: COMPACT_BUTTON_HEIGHT + 18
            }}
        >
            {/* ──────── Bloque de selección / configuraciones ──────── */}
            <ButtonSection
                style={{
                    display: 'flex',
                    flex: '0 0 auto',
                    minWidth: 0
                }}
            >
                <ButtonGroup
                    style={{
                        display: 'flex',
                        flexWrap: 'nowrap',
                        alignItems: 'center',
                        gap: 8,
                        minWidth: 'min-content'
                    }}
                >
                    {/* Dropdown de análisis */}
                    <Tooltip title={disableAnalysisSelection ? 'Bloqueado por contexto' : 'Seleccionar análisis'}>
                        <StyledButton
                            variant="outlined"
                            size="small"
                            onClick={openMenu}
                            endIcon={<ExpandMoreIcon fontSize="small" />}
                            disabled={disableAnalysisSelection}
                            style={{
                                ...baseCompactButtonStyle,
                                borderWidth: 2,
                                maxWidth: 260,
                                flex: '0 0 auto'
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                                {(() => {
                                    const active = analysisMenuItems.find((i) => i.value === selectedAnalysisType);
                                    return active ? active.icon : <AnalysisIcon fontSize="small" />;
                                })()}
                                <div
                                    style={{
                                        textAlign: 'left',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        overflow: 'hidden'
                                    }}
                                >
                                    <span
                                        style={{
                                            fontWeight: 700,
                                            lineHeight: 1.1,
                                            fontSize: 12
                                        }}
                                    >
                                        Seleccionar análisis
                                    </span>
                                    <span
                                        style={{
                                            fontSize: 11,
                                            opacity: 0.7,
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden'
                                        }}
                                    >
                                        {valueToLabel(selectedAnalysisType, analysisMenuItems)}
                                    </span>
                                </div>
                            </div>
                        </StyledButton>
                    </Tooltip>

                    {/* Selector Tipo de producto (Select compacto) */}
                    {showMaterialSelector && (
                        <StyledSelect
                            value={materialType}
                            onChange={handleMaterialTypeChange}
                            displayEmpty
                            size="small"
                            style={{
                                minWidth: 120,
                                fontSize: 12,
                                height: COMPACT_BUTTON_HEIGHT,
                                flex: '0 0 auto'
                            }}
                            renderValue={(val) => {
                                if (!val) return 'Líquido / Sólido';
                                return val === 'LIQUIDO' ? 'Líquido' : 'Sólido';
                            }}
                        >
                            <MenuItem value="LIQUIDO">Líquido</MenuItem>
                            <MenuItem value="SOLIDO">Sólido</MenuItem>
                        </StyledSelect>
                    )}

                    {/* Selector Maquinaria (Select compacto) */}
                    {showMachinerySelector && (
                        <StyledSelect
                            value={machineryType}
                            onChange={(e) => handleMachineryTypeChange(e.target.value)}
                            displayEmpty
                            size="small"
                            style={{
                                minWidth: 130,
                                fontSize: 12,
                                height: COMPACT_BUTTON_HEIGHT,
                                flex: '0 0 auto'
                            }}
                            renderValue={(val) => {
                                if (!val) return 'Maquinaria';
                                return machineryLabel(val);
                            }}
                        >
                            {machineryOptions.map((opt) => {
                                const sinSaldo = creditosActivos
                                    && !creditoDe(opt.value, 'CONVENCIONAL')?.habilitado
                                    && !creditoDe(opt.value, 'VAR')?.habilitado;
                                return (
                                    <MenuItem key={opt.value} value={opt.value} disabled={sinSaldo}>
                                        {opt.label}{sinSaldo ? ' (sin hectáreas)' : ''}
                                    </MenuItem>
                                );
                            })}
                        </StyledSelect>
                    )}

                    {/* Modo normal o VAR, dentro del mismo análisis.
                        APS: VAR / Tasa variable aplica a Dron y Avioneta. Herbicidas: aplica a Dron y Autopropulsado. */}
                    {showApplicationModeSelector && (
                        <StyledSelect
                            value={apsApplicationMode}
                            onChange={(e) => handleApsApplicationModeChange(e, e.target.value)}
                            displayEmpty
                            size="small"
                            style={{
                                minWidth: 145,
                                fontSize: 12,
                                height: COMPACT_BUTTON_HEIGHT,
                                flex: '0 0 auto',
                                border: apsApplicationMode === 'VAR' ? '1px solid var(--primary-color)' : undefined,
                                fontWeight: 700
                            }}
                            renderValue={(val) => {
                                if (val === 'VAR') return 'Modo: VRA';
                                return 'Modo: Normal';
                            }}
                        >
                            {[['CONVENCIONAL', 'Modo: Normal'], ['VAR', 'Modo: VRA / Tasa variable']].map(([value, label]) => {
                                const credito = creditosActivos ? creditoDe(machineryType, value) : null;
                                const sinSaldo = creditosActivos && !credito?.habilitado;
                                return (
                                    <MenuItem key={value} value={value} disabled={sinSaldo}>
                                        {label}
                                        {creditosActivos && (sinSaldo
                                            ? ' (sin hectáreas)'
                                            : ` · ${formatHa(credito.hectareasDisponibles)}`)}
                                    </MenuItem>
                                );
                            })}
                        </StyledSelect>
                    )}

                    {/* Saldo de hectáreas del producto elegido (equipo + tasa) */}
                    {creditosActivos && creditoActual && (
                        <Tooltip
                            arrow
                            title={`${creditoActual.label}: ${formatHa(creditoActual.hectareasConsumidas)} usadas de ${formatHa(creditoActual.hectareasAsignadas)} compradas`}
                        >
                            <Chip
                                size="small"
                                label={creditoActual.habilitado
                                    ? `Saldo: ${formatHa(creditoActual.hectareasDisponibles)}`
                                    : (creditoActual.nivel === 'agotado' ? 'Sin saldo' : 'No contratado')}
                                color={creditoActual.nivel === 'ok' ? 'success'
                                    : creditoActual.nivel === 'bajo' ? 'warning' : 'error'}
                                variant={creditoActual.nivel === 'ok' ? 'outlined' : 'filled'}
                                style={{ height: COMPACT_BUTTON_HEIGHT, fontWeight: 700, flex: '0 0 auto' }}
                            />
                        </Tooltip>
                    )}

                    {/* Menú de selección de análisis */}
                    <Menu
                        anchorEl={anchorEl}
                        open={analysisMenuOpen}
                        onClose={closeMenu}
                        elevation={6}
                        PaperProps={{
                            sx: { minWidth: 260, borderRadius: 2, py: 0.25 }
                        }}
                    >
                        {(() => {
                            const activeValue = selectedAnalysisType;
                            const firstIndexOfActive = analysisMenuItems.findIndex((i) => i.value === activeValue);
                            return (
                                <MenuList dense disablePadding>
                                    {analysisMenuItems.map((item, idx) => {
                                        const isActive = idx === firstIndexOfActive;
                                        return (
                                            <MenuItem
                                                key={`${item.label}-${idx}`}
                                                selected={isActive}
                                                onClick={() => {
                                                    handleAnalysisTypeChange({ target: { value: item.value } });
                                                    closeMenu();
                                                }}
                                                sx={{ py: 0.5 }}
                                            >
                                                <ListItemIcon sx={{ minWidth: 28 }}>{item.icon}</ListItemIcon>
                                                <ListItemText
                                                    primary={
                                                        <span
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 6,
                                                                fontSize: 12
                                                            }}
                                                        >
                                                            {item.label}
                                                            {isActive && item.label !== 'APS' && (
                                                                <Chip
                                                                    size="small"
                                                                    icon={<CheckIcon fontSize="small" />}
                                                                    label="Activo"
                                                                />
                                                            )}
                                                        </span>
                                                    }
                                                    secondary={
                                                        <span
                                                            style={{
                                                                fontSize: 11,
                                                                opacity: 0.7
                                                            }}
                                                        >
                                                            {item.hint}
                                                        </span>
                                                    }
                                                />
                                            </MenuItem>
                                        );
                                    })}
                                </MenuList>
                            );
                        })()}
                    </Menu>
                </ButtonGroup>
            </ButtonSection>

            {/* ──────── Bloque de acciones principales ─ iconos + CTAs ──────── */}
            <ButtonSection
                style={{
                    display: 'flex',
                    flex: '0 0 auto',
                    minWidth: 0,
                    marginLeft: 'auto'
                }}
            >
                <ButtonGroup
                    style={{
                        display: 'flex',
                        flexWrap: 'nowrap',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 6,
                        minWidth: 'min-content'
                    }}
                >
                    {/* Datos fertilización (icono naranja/verde) */}
                    {selectedAnalysisType === 'FERTILIZACION' && (txtFileUploaded || zipHasPoints) && (
                        <Tooltip
                            title={
                                anchoFajaConfirmado
                                    ? `Datos guardados: ${localStorage.getItem('fertilizacion_ancho_faja')} m`
                                    : zipHasPoints
                                        ? 'OBLIGATORIO: Ingrese y guarde el ancho de faja (ZIP con puntos)'
                                        : 'OBLIGATORIO: Ingrese y guarde el ancho de faja'
                            }
                        >
                            <StyledButton
                                variant="contained"
                                size="small"
                                onClick={() => {
                                    setFertModalMode(zipHasPoints ? 'ZIP_POINTS' : 'TXT');
                                    setOpenFertModal(true);
                                }}
                                style={{
                                    ...iconOnlyCompactStyle,
                                    backgroundColor: anchoFajaConfirmado ? '#4caf50' : 'var(--primary-color)',
                                    color: '#fff',
                                    flex: '0 0 auto'
                                }}
                            >
                                {renderIconLabel(<StraightenIcon fontSize="small" />, 'Faja')}
                            </StyledButton>
                        </Tooltip>
                    )}

                    {/* Datos APS / Herbicidas (manual) */}
                    {isAerialLikeAnalysis && (
                        <Tooltip
                            title={
                                apsRowsCount > 0
                                    ? `Datos de ${isHerbicidasAnalysis ? 'herbicidas' : 'aplicaciones aéreas'} guardados (${apsRowsCount}) — Click para editar`
                                    : `Obligatorio: ingresar manualmente los datos de ${isHerbicidasAnalysis ? 'Herbicidas' : 'Aplicaciones Aéreas'}`
                            }
                        >
                            <StyledButton
                                variant="contained"
                                size="small"
                                onClick={() => setOpenApsModal(true)}
                                style={{
                                    ...iconOnlyCompactStyle,
                                    backgroundColor: apsRowsCount > 0 ? '#4caf50' : '#111',
                                    color: '#fff',
                                    flex: '0 0 auto'
                                }}
                            >
                                {renderIconLabel(<TableChartIcon fontSize="small" />, 'Datos')}
                            </StyledButton>
                        </Tooltip>
                    )}

                    {/* Datos finca maleza */}
                    {selectedAnalysisType === 'DETECCION_MALEZA' && (
                        <Tooltip
                            title={
                                metadatosCompletados
                                    ? 'Datos de la finca completados ✓ - Click para editar'
                                    : 'OBLIGATORIO: Complete los datos de la finca (zafra, tercio, fecha)'
                            }
                        >
                            <StyledButton
                                variant="contained"
                                size="small"
                                onClick={() => setOpenMetadatosModal(true)}
                                style={{
                                    ...iconOnlyCompactStyle,
                                    backgroundColor: metadatosCompletados ? '#4caf50' : 'var(--primary-color)',
                                    color: '#fff',
                                    flex: '0 0 auto'
                                }}
                            >
                                {renderIconLabel(<ExcelIcon fontSize="small" />, 'Finca')}
                            </StyledButton>
                        </Tooltip>
                    )}

                    {/* Subir Plantilla (genérico) */}
                    {canUseDataTemplate && (
                        <Tooltip
                            title={
                                !selectedAnalysisType
                                    ? 'Selecciona un análisis'
                                    : uploadedCsvFileName || 'Subir plantilla (CSV/Excel)'
                            }
                        >
                            <StyledButton
                                variant="contained"
                                size="small"
                                component="label"
                                disabled={
                                    forceEnableUploadTemplate
                                        ? false
                                        : !selectedAnalysisType ||
                                        selectedAnalysisType === 'CONTEO_PALMA' ||
                                        selectedAnalysisType === 'CONTEO_AGAVE'
                                }
                                style={
                                    !selectedAnalysisType ||
                                    selectedAnalysisType === 'CONTEO_PALMA' ||
                                    selectedAnalysisType === 'CONTEO_AGAVE'
                                        ? iconDisabledStyle
                                        : iconDarkStyle
                                }
                            >
                                {renderIconLabel(<ExcelIcon fontSize="small" />, 'Plantilla')}
                                <VisuallyHiddenInput
                                    type="file"
                                    accept=".csv,.xls,.xlsx"
                                    onChange={handleUploadTemplate}
                                />
                            </StyledButton>
                        </Tooltip>
                    )}

                    {/* Subir SHP/ZIP/TXT/TIF */}
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', flex: '0 0 auto' }}>
                        <Tooltip
                            title={
                                isZipValidating
                                    ? 'Validando ZIP…'
                                    : !selectedAnalysisType
                                        ? 'Selecciona un análisis'
                                        : selectedAnalysisType === 'DETECCION_MALEZA' ||
                                        selectedAnalysisType === 'CONTEO_PALMA' ||
                                        selectedAnalysisType === 'CONTEO_AGAVE'
                                            ? hasApplicationFile
                                                ? `Archivo seleccionado: ${applicationFileName}`
                                                : 'Sube una imagen TIF para análisis'
                                            : hasApplicationFile
                                                ? `Archivo seleccionado: ${applicationFileName}`
                                                : isVarModeActive
                                                    ? 'Sube la aplicación VRA: puntos (SHP comprimido en ZIP), franjas (KML/KMZ/ZIP) o el .log del controlador'
                                                    : 'Sube un shapefile (ZIP) o archivo de coordenadas (TXT)'
                            }
                        >
                            <StyledButton
                                variant="contained"
                                size="small"
                                component="label"
                                disabled={
                                    isZipValidating ||
                                    !selectedAnalysisType ||
                                    selectedAnalysisType === 'COSECHA_MECANICA'
                                }
                                style={
                                    isZipValidating ||
                                    !selectedAnalysisType ||
                                    selectedAnalysisType === 'COSECHA_MECANICA'
                                        ? iconDisabledStyle
                                        : hasApplicationFile
                                            ? { ...iconOnlyCompactStyle, backgroundColor: '#4caf50', color: '#fff' }
                                            : iconDarkStyle
                                }
                                onClick={(ev) => {
                                    if (isZipValidating) {
                                        ev.preventDefault();
                                        ev.stopPropagation();
                                    }
                                }}
                            >
                                {renderIconLabel(
                                    isZipValidating ? (
                                        <CircularProgress size={16} thickness={5} />
                                    ) : (
                                        <ShapefileIcon fontSize="small" />
                                    ),
                                    isVarModeActive
                                        ? 'Aplicación VRA'
                                        : isHerbicidasAnalysis
                                            ? 'Franjas'
                                            : getUploadButtonText().includes('Imagen')
                                                ? 'TIF'
                                                : 'KML/SHP'
                                )}
                                <VisuallyHiddenInput
                                    type="file"
                                    accept={getAcceptedFileTypes()}
                                    onChange={handleUploadShp}
                                    disabled={isZipValidating}
                                />
                            </StyledButton>
                        </Tooltip>
                    </div>

                    {/* Polígonos de maleza para Tasa Variable / VAR (Aplicaciones Aéreas y Herbicidas) */}
                    {isVarModeActive && (
                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', flex: '0 0 auto' }}>
                            <Tooltip title={uploadedApsTargetPolygonFileName || 'Subir polígonos de maleza: SHP/ZIP o KML/KMZ'}>
                                <StyledButton
                                    variant="contained"
                                    size="small"
                                    component="label"
                                    style={{
                                        ...primaryCompactButtonStyle,
                                        backgroundColor: selectedApsTargetPolygonFile ? '#4caf50' : 'var(--primary-color)',
                                        color: '#fff',
                                        flex: '0 0 auto'
                                    }}
                                >
                                    <ShapefileIcon fontSize="small" style={{ marginRight: 4 }} />
                                    {/* Verde = ya cargada; el nombre del archivo va en el tooltip. */}
                                    Maleza
                                    <VisuallyHiddenInput type="file" accept=".zip,.kml,.kmz" onChange={handleUploadApsTargetPolygon} />
                                </StyledButton>
                            </Tooltip>
                        </div>
                    )}

                    {/* CTA: Generar Mapas (naranja) */}
                    <TooltipStyled title="Configurar filtros y generar mapas">
                        <StyledButton
                            variant="contained"
                            size="small"
                            onClick={openFilterDialog}
                            disabled={
                                !processingFinished ||
                                selectedAnalysisType === 'DETECCION_MALEZA'
                            }
                            style={{
                                ...primaryCompactButtonStyle,
                                backgroundColor:
                                    !processingFinished ||
                                    selectedAnalysisType === 'DETECCION_MALEZA'
                                        ? '#b6b6b6'
                                        : 'var(--primary-color)',
                                color: '#fff',
                                flex: '0 0 auto'
                            }}
                        >
                            <AnalysisIcon fontSize="small" style={{ marginRight: 4 }} />
                            Mapas
                        </StyledButton>
                    </TooltipStyled>

                    {/* Descargar plantilla (icono oscuro) */}
                    {!isApsAnalysis && (
                        <Tooltip
                            title={
                                !selectedAnalysisType && !selectedSidebarOption
                                    ? 'Selecciona un análisis'
                                    : 'Descargar plantilla'
                            }
                        >
                            <StyledButton
                                variant="contained"
                                size="small"
                                href={templatePath}
                                download
                                disabled={
                                    forceEnableUploadTemplate
                                        ? false
                                        : (!selectedAnalysisType && !selectedSidebarOption) ||
                                        selectedAnalysisType === 'DETECCION_MALEZA'
                                }
                                style={
                                    (!selectedAnalysisType && !selectedSidebarOption) ||
                                    selectedAnalysisType === 'DETECCION_MALEZA'
                                        ? iconDisabledStyle
                                        : iconDarkStyle
                                }
                            >
                                {renderIconLabel(<TemplateIcon fontSize="small" />, 'Descarga')}
                            </StyledButton>
                        </Tooltip>
                    )}

                    {/* Calidad (solo conteo/maleza) */}
                    {requiereCalidad && (
                        <StyledSelect
                            value={calidad}
                            onChange={(e) => setCalidad(e.target.value)}
                            displayEmpty
                            size="small"
                            style={{
                                minWidth: 120,
                                fontSize: 12,
                                height: COMPACT_BUTTON_HEIGHT,
                                flex: '0 0 auto'
                            }}
                            renderValue={(val) => {
                                if (!val) return 'Calidad';
                                if (val === 'baja') return 'Calidad baja';
                                if (val === 'media') return 'Calidad media';
                                return 'Calidad alta';
                            }}
                        >
                            <MenuItem value="baja">Calidad Baja</MenuItem>
                            <MenuItem value="media">Calidad Media</MenuItem>
                            <MenuItem value="alta">Calidad Alta</MenuItem>
                        </StyledSelect>
                    )}

                    {/* CTA: Ejecutar análisis (naranja, texto corto) */}
                    <TooltipStyled
                        title={
                            selectedAnalysisType === 'DETECCION_MALEZA' && !metadatosCompletados
                                ? 'Completa los datos de finca'
                                : selectedAnalysisType === 'FERTILIZACION' &&
                                (txtFileUploaded || zipHasPoints) &&
                                !anchoFajaConfirmado
                                    ? 'Ingrese el ancho de faja'
                                    : productoBloqueado
                                        ? motivoBloqueoCredito
                                        : selectedAnalysisType === 'APLICACIONES_AEREAS' && apsApplicationMode === 'VAR' && !selectedApsTargetPolygonFile
                                            ? 'Sube los polígonos de maleza para VRA'
                                            : 'Ejecutar análisis'
                        }
                    >
                        <StyledButton
                            variant="contained"
                            size="small"
                            onClick={() => {
                                // Mapeos de maquinaria: antes se pide el nombre y a quién enviar el PDF.
                                if (TIPOS_CON_CREDITOS.has(selectedAnalysisType)) {
                                    setDatosEnvioAbierto(true);
                                    return;
                                }
                                execBash(false, calidad, materialType, machineryType);
                            }}
                            disabled={
                                !execBashEnabled ||
                                (selectedAnalysisType === 'DETECCION_MALEZA' &&
                                    !metadatosCompletados) ||
                                (selectedAnalysisType === 'FERTILIZACION' &&
                                    (txtFileUploaded || zipHasPoints) &&
                                    !anchoFajaConfirmado) ||
                                (selectedAnalysisType === 'APLICACIONES_AEREAS' && apsApplicationMode === 'VAR' && !selectedApsTargetPolygonFile) ||
                                productoBloqueado ||
                                isZipValidating
                            }
                            style={{
                                ...primaryCompactButtonStyle,
                                backgroundColor:
                                    !execBashEnabled ||
                                    (selectedAnalysisType === 'DETECCION_MALEZA' &&
                                        !metadatosCompletados) ||
                                    (selectedAnalysisType === 'FERTILIZACION' &&
                                        (txtFileUploaded || zipHasPoints) &&
                                        !anchoFajaConfirmado) ||
                                    (selectedAnalysisType === 'APLICACIONES_AEREAS' &&
                                        apsApplicationMode === 'VAR' &&
                                        !selectedApsTargetPolygonFile) ||
                                    productoBloqueado ||
                                    isZipValidating
                                        ? '#b6b6b6'
                                        : 'var(--primary-color)',
                                color: '#fff',
                                flex: '0 0 auto'
                            }}
                        >
                            <AnalysisIcon fontSize="small" style={{ marginRight: 4 }} />
                            Analizar
                        </StyledButton>
                    </TooltipStyled>

                    {/* Generar informe PDF. Si el análisis terminó y aún no se ha generado, el tooltip guía al usuario sin interrumpirlo. */}
                    <Tooltip title={pdfTooltipTitle} arrow>
                        <span>
                            <IconButtonStyled
                                size="small"
                                onClick={handleGeneratePdf}
                                disabled={isGeneratingReport}
                                style={isGeneratingReport ? iconDisabledStyle : pdfButtonStyle}
                            >
                                {isGeneratingReport
                                    ? <CircularProgress size={18} />
                                    : renderIconLabel(<PictureAsPdfIcon fontSize="small" />, 'PDF')
                                }
                            </IconButtonStyled>
                        </span>
                    </Tooltip>

                    {/* Empezar de cero sin cerrar sesión: recarga /mapeo limpio y deja
                        atrás el análisis que está en pantalla (también el reabierto). */}
                    <Tooltip title="Nuevo análisis: limpia el mapa para empezar otro" arrow>
                        <span>
                            <IconButtonStyled
                                size="small"
                                onClick={handleNuevoAnalisis}
                                disabled={isGeneratingReport}
                                style={isGeneratingReport ? iconDisabledStyle : iconDarkStyle}
                            >
                                {renderIconLabel(<RestartAltIcon fontSize="small" />, 'Nuevo')}
                            </IconButtonStyled>
                        </span>
                    </Tooltip>
                </ButtonGroup>
            </ButtonSection>

            {isGeneratingReport && <Loader />}

            {/* ────────── Modal comentario PDF ────────── */}
            <Dialog
                open={openCommentModal}
                onClose={handleCloseComment}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>Agregar comentario al reporte</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        multiline
                        rows={8}
                        label="Comentario"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" size="small" onClick={handleCloseComment}>
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleSubmitComment}
                        style={{ backgroundColor: 'var(--primary-color)', color: '#fff' }}
                    >
                        Generar reporte
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ────────── Modal maleza ────────── */}
            <MetadatosDeteccionMalezaModal
                open={openMetadatosModal}
                onClose={() => setOpenMetadatosModal(false)}
                onConfirm={onMalezaConfirm}
            />

            <DatosEnvioAnalisisDialog
                open={datosEnvioAbierto}
                idUsuario={idUsuarioActual}
                onCancel={() => setDatosEnvioAbierto(false)}
                onConfirm={() => {
                    setDatosEnvioAbierto(false);
                    execBash(false, calidad, materialType, machineryType);
                }}
            />

            {/* ────────── Modal fertilización ────────── */}
            <FertilizacionModal
                open={openFertModal}
                onClose={() => setOpenFertModal(false)}
                onConfirm={onFertConfirm}
                isProcessing={false}
                mode={fertModalMode}
                initialAnchoFaja={(() => {
                    const v = localStorage.getItem('fertilizacion_ancho_faja');
                    const n = v ? Number(v) : '';
                    return Number.isFinite(n) && n > 0 ? String(n) : '';
                })()}
                initialRangos={(() => {
                    try {
                        const arr = JSON.parse(
                            localStorage.getItem('fertilizacion_rangos') ||
                            '[null,null,null]'
                        );
                        const [min, med, max] = Array.isArray(arr)
                            ? arr
                            : [null, null, null];
                        return {
                            rangoMin: min ?? '',
                            rangoMedio: med ?? '',
                            rangoMax: max ?? ''
                        };
                    } catch {
                        return { rangoMin: '', rangoMedio: '', rangoMax: '' };
                    }
                })()}
                templates={propertyTemplates}
                initialCustomProps={customProps}
            />

            {/* ────────── Modal aplicaciones aéreas ────────── */}
            <AplicacionesAereasModal
                open={openApsModal}
                onClose={() => setOpenApsModal(false)}
                onConfirm={onApsConfirm}
                initialRows={apsInitialRows}
                title={isHerbicidasAnalysis ? 'Datos de Herbicidas' : 'Datos de Aplicaciones Aéreas'}
                description={isHerbicidasAnalysis
                    ? 'Guarda aquí los datos manuales de la aplicación de herbicidas antes de ejecutar el análisis. Velocidad y altura se calculan en km/h y msnm desde el archivo de aplicación (franjas o puntos).'
                    : null}
            />
        </ToolbarContainer>
    );
};

export default React.memo(ToolbarComponent);
